import { useState, useCallback } from "react";
import { ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE } from "constants/ActionTypes";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Disclaimer from "components/MediaLibrary/SourceMedia/Disclaimer/Disclaimer";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "actions/modal";
import { addCollectionAssetsFromFeed } from "actions/mediaLibrary";
import { getRoutinesLoading } from "selectors/commonSelectors";
import {
  getSelectedFeedItems,
  getSelectedHashtagFeedCollection,
} from "selectors/hashtagFeedSelectors";
import type { ReduxRootState } from "types/redux-store";

interface FeedItem {
  id: string;
  [key: string]: unknown;
}

interface Collection {
  _id: string;
  name: string;
}

const AddToCollectionModal = () => {
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const selectedFeedItems = useSelector(getSelectedFeedItems) as FeedItem[];
  const selectedHashtagFeedCollection = useSelector(
    getSelectedHashtagFeedCollection
  ) as Collection | null;
  const dispatch = useDispatch();
  const isFetching = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE])
  );

  const handleClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const submitAssets = useCallback(() => {
    if (!selectedHashtagFeedCollection) return;
    dispatch(
      addCollectionAssetsFromFeed({
        collectionId: selectedHashtagFeedCollection._id,
        feedItems: selectedFeedItems,
        location: "library",
      })
    );
  }, [dispatch, selectedHashtagFeedCollection, selectedFeedItems]);

  const assetCount = selectedFeedItems?.length ?? 0;
  const collectionName = selectedHashtagFeedCollection?.name ?? "Unknown";

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[640px] p-0" showCloseButton={false}>
        <div className="flex flex-col">
          {/* Header */}
          <DialogHeader className="px-6 py-3">
            <DialogTitle className="font-gotham text-lg font-semibold text-neutral-900">
              Confirm Saving to Collection
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-neutral-600 mt-2">
              Review the assets you&apos;re about to add and accept the terms
              below.
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 pb-6 space-y-4">
            {/* Summary */}
            <p className="font-inter text-base text-neutral-700">
              You are about to add{" "}
              <span className="text-blue-600 font-semibold">{assetCount}</span>{" "}
              asset{assetCount === 1 ? "" : "s"} from Instagram to collection:{" "}
              <span className="text-blue-600 font-semibold">
                {collectionName}
              </span>
              .
            </p>

            {/* Disclaimer */}
            <div>
              <Disclaimer onAccept={() => setAcceptedDisclaimer(true)} />
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="flex flex-row items-center justify-between sm:justify-between border-t border-neutral-200 px-6 py-3">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="text-neutral-600 hover:text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </Button>
            <Button
              variant="blue-primary"
              disabled={assetCount === 0 || !acceptedDisclaimer || isFetching}
              onClick={submitAssets}
            >
              {isFetching ? (
                <>
                  <Spinner size="sm" variant="inverted" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddToCollectionModal;
